import { z } from "zod";
import { IdSchema, TimestampSchema, WpIdSchema } from "./primitives.ts";

/**
 * Where an agent run is, as the runner (WP-17) sees it.
 * - `starting`: its Container is starting, or it waits for the spare machine.
 * - `working`: the agent is working.
 * - `waiting_on_negotiation`: the agent has an unsettled blocking overlap.
 * - `done`: the agent requested a land and was stopped.
 * - `failed`: a budget ran out, or the agent hung, crashed or stopped without requesting a land.
 *
 * `done` and `failed` are terminal.
 */
export const AgentRunStatusSchema = z.enum(["starting", "working", "waiting_on_negotiation", "done", "failed"]);
/** See {@link AgentRunStatusSchema}. */
export type AgentRunStatus = z.infer<typeof AgentRunStatusSchema>;

/** One agent working one work package once, in a Cloudflare Container or on the spare machine. */
export const AgentRunSchema = z.object({
  /** The runner's id for the run. */
  id: IdSchema,
  wpId: WpIdSchema,
  /** Null until the run is launched. */
  agentName: z.string().min(1).max(64).nullable(),
  /** Where the agent runs. The coordinator treats both the same. */
  runner: z.enum(["container", "machine"]),
  /** 1 for a package's first run, then 2, 3... */
  attempt: z.number().int().positive(),
  status: AgentRunStatusSchema,
  /** Why it is in its status, in a sentence. */
  reason: z.string().max(500),
  /** The agent's session, once it has joined. */
  sessionId: IdSchema.nullable(),
  budget: z.object({ minutes: z.number().positive(), tokens: z.number().int().positive() }),
  /** Model use so far: weighted tokens (cache reads count a tenth) and calls. */
  usage: z.object({ tokens: z.number().int().min(0), requests: z.number().int().min(0) }),
  startedAt: TimestampSchema,
  endedAt: TimestampSchema.nullable(),
});
/** See {@link AgentRunSchema}. */
export type AgentRun = z.infer<typeof AgentRunSchema>;
